import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { getAllFundPayments } from '../../../../../lib/api/funds';
import GlobalSearcher, {
  Filter,
} from '../../../../../lib/common/GlobalSearcher';
import CenteredCircularProgress from '../../../../../lib/designSystem/CircularProgress';
import HeaderWithActions, {
  CustomButtonProps,
} from '../../../../../lib/designSystem/HeaderWithActions';
import TableWithPagination from '../../../../../lib/designSystem/TableWithPagination';
import { showAlert } from '../../../../../lib/functions/utils';
import RevertPaymentDialog from './components/RevertPaymentDialog';

const headers: { key: string; label: string; type?: any }[] = [
  { key: 'id', label: 'Id', type: 'numeric-id' },
  { key: 'factoringResellId', label: 'Id venta', type: 'numeric-id' },
  { key: 'folio', label: 'Folio' },
  { key: 'businessName', label: 'Cliente' },
  { key: 'stakeholderName', label: 'Deudor' },
  { key: 'fundName', label: 'Fondo' },
  { key: 'date', label: 'Fecha', type: 'date' },
  { key: 'amount', label: 'Monto', type: 'money' },
];

const FactoringFundReSellPayments: React.FC = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  });
  const [payments, setPayments] = useState<any[]>([]);
  const [openRevertModal, setOpenRevertModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<any>(null);
  const [searchResellId, setSearchResellId] = useState('');
  const [searchFolio, setSearchFolio] = useState('');
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [fundId, setFundId] = useState<string | null>(null);
  const [selectedFund, setSelectedFund] = useState<any>(null);
  const [selectedSortBy, setSelectedSortBy] = useState<string>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const generateQuery = ({
    page,
    pageSize,
    exportComplete,
  }: {
    page: number;
    pageSize: number;
    exportComplete?: boolean;
  }) => {
    let query = '';
    if (page) query += `page=${page}&`;
    if (pageSize) query += `pageSize=${pageSize}&`;
    if (searchResellId) query += `resellId=${searchResellId}&`;
    if (searchFolio) query += `folio=${searchFolio}&`;
    if (startDate) query += `startDate=${startDate}&`;
    if (endDate) query += `endDate=${endDate}&`;
    if (fundId) query += `fundId=${fundId}&`;
    if (selectedSortBy) query += `sortBy=${selectedSortBy}&`;
    if (sortOrder) query += `sortOrder=${sortOrder}&`;
    if (exportComplete) query += `all=true&`;
    return query;
  };

  const fetchPayments = async ({
    page,
    pageSize,
    exportComplete,
  }: {
    page: number;
    pageSize: number;
    exportComplete?: boolean;
  }) => {
    setLoading(true);
    try {
      const query = generateQuery({ page, pageSize, exportComplete });
      const response = await getAllFundPayments(dispatch, query);
      if (!exportComplete) {
        setPayments(response.data);
        setPagination(response.pagination);
      }
      setLoading(false);
      return response;
    } catch (error) {
      console.error(error);
      showAlert(dispatch, 'error', 'Error al obtener los pagos');
    }
  };

  const setUp = async () => {
    await fetchPayments({ page: 1, pageSize: 10 });
  };

  useEffect(() => {
    void setUp();
  }, []);

  const handleRevertPayment = (id: number) => {
    const payment = payments.find((payment) => payment.id === id);
    setSelectedPayment(payment);
    setOpenRevertModal(true);
  };

  const actions = [
    {
      action: handleRevertPayment,
      actionLabel: 'Reversar',
      canShow: () => true,
    },
  ];

  const filters: Filter[] = [
    {
      type: 'text',
      label: 'Id venta',
      value: searchResellId,
      setter: setSearchResellId,
    },
    {
      type: 'text',
      label: 'Folio',
      value: searchFolio,
      setter: setSearchFolio,
    },
    {
      type: 'date',
      label: 'Fecha',
      value: startDate,
      setter: setStartDate,
      dateKey: 'startDate',
      pairKey: 'endDate',
    },
    {
      type: 'date',
      label: 'Fecha',
      value: endDate,
      setter: setEndDate,
      dateKey: 'endDate',
      pairKey: 'startDate',
    },
    {
      type: 'autocomplete',
      label: 'Fondo',
      value: fundId,
      key: 'fundName',
      setter: (value: string | null) => {
        setFundId(value);
      },
      selectedValue: selectedFund,
      setSelectedValue: (value: any) => {
        setSelectedFund(value);
        if (value?.object?.id) {
          setFundId(value.object.id);
        } else {
          setFundId(null);
        }
      },
    },
  ];

  const buttons: CustomButtonProps[] = [
    {
      name: 'Buscar',
      color: 'primary',
      action: () => {},
      customButton: (
        <GlobalSearcher
          filters={filters}
          triggerSearch={() => fetchPayments({ page: 1, pageSize: 10 })}
          dispatch={dispatch}
          selectedSortBy={selectedSortBy}
          setSelectedSortBy={setSelectedSortBy}
          sortOptions={[{ value: 'date', label: 'Fecha' }]}
          sortOrder={sortOrder}
          setSortOrder={setSortOrder}
        />
      ),
    },
  ];

  if (loading) return <CenteredCircularProgress />;

  return (
    <div>
      <HeaderWithActions
        title="Acá encontrarás los avisos de pago recibidos de ventas a fondos."
        variant="body1"
        buttons={buttons}
      />
      <TableWithPagination
        data={payments}
        setData={setPayments}
        dataHeaders={headers}
        pagination={pagination}
        setPagination={setPagination}
        searchData={fetchPayments}
        actions={actions}
        exportToFile="xlsx"
        exportComplete
        setLoading={setLoading}
      />
      {openRevertModal && (
        <RevertPaymentDialog
          open={openRevertModal}
          setOpen={setOpenRevertModal}
          payment={selectedPayment}
          setUp={setUp}
          bigSetUp={setUp}
        />
      )}
    </div>
  );
};

export default FactoringFundReSellPayments;
